import { NavSectionKey } from './navSections';

/**
 * Human-readable labels for every NavSectionKey. Used in admin / settings
 * UIs so we never display raw `organisation.businessServices`-style keys
 * to end users. Keep this in sync with `navSectionKeys` — adding a key
 * there without an entry here falls back to the raw key.
 */
export const navSectionLabels: Record<NavSectionKey, string> = {
  overview: 'Dashboard',
  // Organisation group
  organisation: 'Organisation',
  'organisation.overview': 'Organisation overview',
  'organisation.businessServices': 'Business services',
  'organisation.businessFunctions': 'Business functions',
  'organisation.enablingServices': 'Enabling services',
  'organisation.assets': 'Assets',
  'organisation.users': 'Users',
  'organisation.customers': 'Customers',
  'organisation.suppliers': 'Suppliers',
  'organisation.interestedParties': 'Interested parties',
  // Governance group
  governance: 'Governance',
  'governance.goals': 'Goals',
  'governance.securityProjects': 'Security projects',
  'governance.kpis': 'KPIs',
  'governance.rasci': 'RASCI',
  'governance.accessMatrix': 'Access matrix',
  'governance.complianceCalendar': 'Compliance calendar',
  'governance.assetCategoryClassification': 'Asset classifications',
  'governance.exceptions': 'Exceptions',
  // Risk group
  risk: 'Risk management',
  'risk.bia': 'BIA',
  'risk.risks': 'Risks',
  'risk.threats': 'Threats',
  'risk.vulnerabilities': 'Vulnerabilities',
  'risk.measures': 'Measures',
  // Incidents group
  incidents: 'Incidents',
  'incidents.incidents': 'Incident records',
  'incidents.scenarios': 'Scenarios',
  'incidents.playbooks': 'Playbooks',
  'incidents.problems': 'Problems',
  // Disaster Recovery group
  disasterRecovery: 'Disaster Recovery',
  'disasterRecovery.tests': 'DRP Tests',
  'disasterRecovery.scenarios': 'DR Scenarios',
  'disasterRecovery.checklists': 'DR Checklists',
  // Compliance group
  compliance: 'Compliance',
  'compliance.gapAnalysis': 'Gap analysis',
  'compliance.soa': 'Statement of Applicability',
  'compliance.assessments': 'Assessments',
  'compliance.systemKpis': 'System KPIs',
  'compliance.policyLibrary': 'Policy library',
  'compliance.frameworkUcf': 'Unified Control Framework',
  'compliance.frameworkDora': 'DORA',
  'compliance.frameworkGdpr': 'GDPR',
  'compliance.frameworkPciDss': 'PCI DSS',
  'compliance.frameworkLaw95': '95/2019',
  'compliance.frameworkLaw69': '69/2018',
  'compliance.frameworkIso27001': 'ISO/IEC 27001',
  // Time tracking group
  timetracking: 'Time tracking',
  'timetracking.myTimesheet': 'My timesheet',
  'timetracking.holidays': 'Holidays',
  'timetracking.reports': 'Reports',
  'timetracking.exports': 'Exports',
  'timetracking.projects': 'Projects',
  // Training group
  training: 'Training',
  'training.videos': 'Videos',
  'training.campaigns': 'Campaigns',
  // Marketplace group
  marketplace: 'Marketplace',
  'marketplace.gen360': 'gen360 catalog',
  // Settings group
  settings: 'Settings',
  'settings.organisation': 'Organisation settings',
  'settings.tree': 'Group structure',
  'settings.functions': 'Functions',
  'settings.emailTemplates': 'Email templates',
  'settings.idNumbering': 'ID numbering',
  'settings.branding': 'Branding',
  'settings.connectors': 'Connectors',
  'settings.sensors': 'Sensors',
  'settings.importExport': 'Import / Export',
  'settings.accessValidation': 'Access validation',
  'settings.team': 'Team members',
  'settings.invites': 'Invites',
  'settings.sso': 'SSO & provisioning',
  'settings.billing': 'Billing'
};

export function getNavSectionLabel(key: NavSectionKey): string {
  return navSectionLabels[key] ?? key;
}
